import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DomHandler } from 'primeng/dom';

export interface EffectiveBoundaries {
  top: number;
  bottom: number;
}

/**
 * Calculates effective visible boundaries for overlays, accounting for fixed/docked
 * headers, topbars, breadcrumbs, and bottom footers.
 * NOTE: Form action bars (like .dynamic-form-sticky-actions) are NOT bottom obstacles;
 * dropdown overlays are designed to float over in-page content and buttons (z-index 10000).
 */
export function getEffectiveBoundaries(targetTop: number = 0): EffectiveBoundaries {
  if (typeof window === 'undefined') {
    return { top: 0, bottom: 0 };
  }

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  let bottomBoundary = viewportHeight;

  // Detect genuine bottom obstacles docked at the bottom of the viewport (app footer, layout footer)
  const bottomObstacles = document.querySelectorAll(
    '[app-footer], .layout-footer-full, .layout-footer'
  );
  for (let i = 0; i < bottomObstacles.length; i++) {
    const el = bottomObstacles[i] as HTMLElement;
    if (el) {
      const rect = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      if (rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden') {
        // Only elements that are actually docked at the bottom edge of the viewport
        if (rect.top > targetTop && rect.bottom >= viewportHeight - 30) {
          if (rect.top < bottomBoundary) {
            bottomBoundary = rect.top;
          }
        }
      }
    }
  }

  // Detect top obstacles (topbar, breadcrumb, fixed headers, dialog header)
  let topBoundary = 0;
  const topObstacles = document.querySelectorAll(
    '.layout-topbar, .topbar, app-topbar, .app-topbar, header.sticky, nav.sticky, .p-dialog-header, app-breadcrumb, .app-breadcrumb, .layout-breadcrumb-container'
  );
  for (let i = 0; i < topObstacles.length; i++) {
    const el = topObstacles[i] as HTMLElement;
    if (el) {
      const rect = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      if (rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden') {
        if (rect.bottom > topBoundary && rect.bottom <= targetTop) {
          topBoundary = rect.bottom;
        }
      }
    }
  }

  return {
    top: topBoundary + 4,
    bottom: Math.max(0, bottomBoundary - 4),
  };
}

@Injectable({
  providedIn: 'root',
})
export class DropdownAutoPositionService {
  private readonly platformId = inject(PLATFORM_ID);
  private initialized = false;

  public init(): void {
    if (!isPlatformBrowser(this.platformId) || this.initialized) {
      return;
    }
    this.initialized = true;
    this.patchDomHandler();
  }

  private patchDomHandler(): void {
    DomHandler.alignOverlay = (
      overlay: any,
      target: any,
      appendTo: any = 'self',
      calculateMinWidth: boolean = true
    ): void => {
      if (!overlay || !target) {
        return;
      }

      if (calculateMinWidth) {
        overlay.style.minWidth = `${DomHandler.getOuterWidth(target)}px`;
      }

      this.autoPositionOverlay(overlay, target, appendTo, true);
    };

    DomHandler.absolutePosition = (element: any, target: any, gutter: boolean = true): void => {
      if (!element || !target) {
        return;
      }
      this.autoPositionOverlay(element, target, 'body', gutter);
    };

    DomHandler.relativePosition = (element: any, target: any, gutter: boolean = true): void => {
      if (!element || !target) {
        return;
      }
      this.autoPositionOverlay(element, target, 'self', gutter);
    };
  }

  public autoPositionOverlay(
    overlay: HTMLElement,
    target: HTMLElement,
    appendTo: any = 'self',
    gutter: boolean = true
  ): void {
    if (!overlay || !target || typeof target.getBoundingClientRect !== 'function') {
      return;
    }

    const targetOffset = target.getBoundingClientRect();
    const targetOuterHeight = target.offsetHeight || targetOffset.height || 32;
    const targetOuterWidth = target.offsetWidth || targetOffset.width || 120;
    const windowScrollTop = DomHandler.getWindowScrollTop();
    const windowScrollLeft = DomHandler.getWindowScrollLeft();
    const viewport = DomHandler.getViewport();

    const panel = (overlay.querySelector?.(
      '.p-select-overlay, .p-dropdown-panel, .p-multiselect-overlay, .p-autocomplete-panel, .p-datepicker-panel, .p-overlay-content'
    ) || overlay) as HTMLElement;

    let measuredHeight = Math.max(
      overlay.offsetHeight || 0,
      panel?.offsetHeight || 0,
      panel?.scrollHeight || 0
    );
    if (measuredHeight <= 0) {
      const hiddenDims = DomHandler.getHiddenElementDimensions(panel || overlay);
      measuredHeight = hiddenDims?.height || 0;
    }

    const measuredWidth = Math.max(
      overlay.offsetWidth || 0,
      panel?.offsetWidth || 0,
      targetOuterWidth
    );

    const boundaries = getEffectiveBoundaries(targetOffset.top);
    const spaceBelow = boundaries.bottom - (targetOffset.top + targetOuterHeight);
    const spaceAbove = targetOffset.top - boundaries.top;

    // Dropdown panel typically needs ~200-240px to display comfortably.
    // Default to opening downwards unless there's genuinely not enough room below.
    const DEFAULT_PANEL_HEIGHT = 220;
    const effectivePanelHeight = measuredHeight > 40 ? measuredHeight : DEFAULT_PANEL_HEIGHT;

    // Dropdown should ONLY flip to opening above if:
    // 1. Space below cannot fit the panel (spaceBelow < effectivePanelHeight and spaceBelow < 200)
    // 2. AND space above has more room than space below (spaceAbove > spaceBelow)
    // 3. AND space above has at least 150px (enough to show the search filter box and options without being clipped by the topbar)
    const fitsBelow = spaceBelow >= effectivePanelHeight || spaceBelow >= 200;
    const shouldOpenAbove = !fitsBelow && spaceAbove > spaceBelow && spaceAbove >= 150;

    // Ensure overlay has high z-index to appear above sticky bars
    const currentZ = parseInt(overlay.style.zIndex || '0', 10);
    if (isNaN(currentZ) || currentZ < 10000) {
      overlay.style.zIndex = '10000';
    }

    if (appendTo !== 'self') {
      // --- Absolute / Teleported to Body ---
      let top: number;
      let origin = 'top';

      if (shouldOpenAbove) {
        origin = 'bottom';
        const heightToUse = measuredHeight > 40 ? measuredHeight : DEFAULT_PANEL_HEIGHT;
        top = targetOffset.top + windowScrollTop - heightToUse - 4;

        // CRITICAL: Top must NEVER be placed behind the topbar/header or off-screen
        const minAllowedTop = windowScrollTop + boundaries.top;
        if (top < minAllowedTop) {
          top = minAllowedTop;
        }

        // Constrain the list wrapper so search box remains visible
        const maxAvailableHeight = targetOffset.top - boundaries.top - 8;
        const listContainer = overlay.querySelector(
          '.p-select-list-container, .p-dropdown-items-wrapper, .p-multiselect-items-wrapper, .p-autocomplete-items-wrapper, .lazy-dropdown-list, .lms-list'
        ) as HTMLElement;
        if (listContainer && maxAvailableHeight > 60) {
          listContainer.style.maxHeight = `${Math.max(50, maxAvailableHeight - 52)}px`;
        }
      } else {
        origin = 'top';
        top = targetOffset.top + targetOuterHeight + windowScrollTop + 2;

        // Constrain the list wrapper if space below is limited
        if (targetOffset.top + targetOuterHeight + effectivePanelHeight > boundaries.bottom) {
          const maxAvailableHeight = boundaries.bottom - (targetOffset.top + targetOuterHeight) - 8;
          const listContainer = overlay.querySelector(
            '.p-select-list-container, .p-dropdown-items-wrapper, .p-multiselect-items-wrapper, .p-autocomplete-items-wrapper, .lazy-dropdown-list, .lms-list'
          ) as HTMLElement;
          if (listContainer && maxAvailableHeight > 60) {
            listContainer.style.maxHeight = `${Math.max(50, maxAvailableHeight - 52)}px`;
          }
        }
      }

      let left: number;
      if (targetOffset.left + measuredWidth > viewport.width) {
        left = Math.max(0, targetOffset.left + windowScrollLeft + targetOuterWidth - measuredWidth);
      } else {
        left = targetOffset.left + windowScrollLeft;
      }

      overlay.style.top = `${top}px`;
      overlay.style.left = `${left}px`;
      overlay.style.transformOrigin = origin;
      if (gutter) {
        overlay.style.marginTop = origin === 'bottom' ? '-2px' : '2px';
      }
    } else {
      // --- Relative Positioning ---
      const getClosestRelativeElement = (el: HTMLElement | null): HTMLElement | null => {
        if (!el) return null;
        return getComputedStyle(el).getPropertyValue('position') === 'relative'
          ? el
          : getClosestRelativeElement(el.parentElement);
      };

      const relativeElement = getClosestRelativeElement(overlay);
      const relativeElementOffset = relativeElement?.getBoundingClientRect() || {
        top: -1 * windowScrollTop,
        left: -1 * windowScrollLeft,
      };

      let top: number;
      let origin = 'top';

      if (shouldOpenAbove) {
        origin = 'bottom';
        const heightToUse = measuredHeight > 40 ? measuredHeight : DEFAULT_PANEL_HEIGHT;
        top = targetOffset.top - relativeElementOffset.top - heightToUse - 4;

        const minAllowedTop = boundaries.top - relativeElementOffset.top;
        if (top < minAllowedTop) {
          top = minAllowedTop;
        }
      } else {
        origin = 'top';
        top = targetOuterHeight + targetOffset.top - relativeElementOffset.top + 2;
      }

      let left: number;
      const horizontalOverflow = targetOffset.left + measuredWidth - viewport.width;
      if (measuredWidth > viewport.width) {
        left = (targetOffset.left - relativeElementOffset.left) * -1;
      } else if (horizontalOverflow > 0) {
        left = targetOffset.left - relativeElementOffset.left - horizontalOverflow;
      } else {
        left = targetOffset.left - relativeElementOffset.left;
      }

      overlay.style.top = `${top}px`;
      overlay.style.left = `${left}px`;
      overlay.style.transformOrigin = origin;
      if (gutter) {
        overlay.style.marginTop = origin === 'bottom' ? '-2px' : '2px';
      }
    }

    // Dynamic readjustment when panel content resizes (e.g. typing in search filter, async data loading)
    if (shouldOpenAbove && typeof ResizeObserver !== 'undefined') {
      const prevObserver = (overlay as any).__autoPosObserver as ResizeObserver | undefined;
      if (prevObserver) {
        prevObserver.disconnect();
      }

      const observer = new ResizeObserver(() => {
        if (!overlay.isConnected || !target.isConnected || overlay.style.display === 'none') {
          observer.disconnect();
          delete (overlay as any).__autoPosObserver;
          return;
        }

        const currentPanel = (overlay.querySelector?.(
          '.p-select-overlay, .p-dropdown-panel, .p-multiselect-overlay, .p-autocomplete-panel, .p-datepicker-panel, .p-overlay-content'
        ) || overlay) as HTMLElement;
        const curH = currentPanel?.offsetHeight || overlay.offsetHeight;
        if (curH > 0) {
          const tOffset = target.getBoundingClientRect();
          const sTop = DomHandler.getWindowScrollTop();
          const b = getEffectiveBoundaries(tOffset.top);
          if (appendTo !== 'self') {
            let nTop = tOffset.top + sTop - curH - 4;
            const minAllowed = sTop + b.top;
            if (nTop < minAllowed) {
              nTop = minAllowed;
            }
            overlay.style.top = `${nTop}px`;
          } else {
            const relEl = (overlay as any).parentElement;
            const rOffset = relEl?.getBoundingClientRect() || { top: -1 * sTop };
            let nTop = tOffset.top - rOffset.top - curH - 4;
            const minAllowed = b.top - rOffset.top;
            if (nTop < minAllowed) {
              nTop = minAllowed;
            }
            overlay.style.top = `${nTop}px`;
          }
          overlay.style.transformOrigin = 'bottom';
        }
      });

      if (panel) {
        observer.observe(panel);
      }
      if (panel !== overlay) {
        observer.observe(overlay);
      }
      (overlay as any).__autoPosObserver = observer;
    } else {
      const prevObserver = (overlay as any).__autoPosObserver as ResizeObserver | undefined;
      if (prevObserver) {
        prevObserver.disconnect();
        delete (overlay as any).__autoPosObserver;
      }
    }
  }
}

